import { useCallback, useEffect, useMemo, useState } from 'react'
import { ralphApi } from '../api'
import { useAppStore } from '../stores/app-store'
import type {
  AccountCredentialStatus,
  AccountSummary,
  CredentialRotationHistory,
  DeliveryHistoryEntry
} from '../shared/types'

type Provider = AccountCredentialStatus['provider']

const PROVIDERS: Provider[] = ['anthropic', 'minimax', 'zai', 'github']
const PROVIDER_LABELS: Record<Provider, string> = {
  anthropic: 'Anthropic',
  minimax: 'MiniMax',
  zai: 'Z.ai',
  github: 'GitHub'
}

const DEFAULT_EXPORT_FIELDS = ['createdAt', 'deliveryType', 'status', 'target', 'responseCode', 'errorMessage']

const formatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short'
})

function formatDate(value?: number | null): string {
  if (!value) return 'never'
  return formatter.format(new Date(value))
}

function buildCredentialMap(credentials: AccountCredentialStatus[]): Record<Provider, AccountCredentialStatus> {
  return PROVIDERS.reduce<Record<Provider, AccountCredentialStatus>>((acc, provider) => {
    acc[provider] = credentials.find((item) => item.provider === provider) ?? {
      provider,
      configured: false
    }
    return acc
  }, {} as Record<Provider, AccountCredentialStatus>)
}

function createEmptyDrafts(): Record<Provider, string> {
  return PROVIDERS.reduce<Record<Provider, string>>((acc, provider) => {
    acc[provider] = ''
    return acc
  }, {} as Record<Provider, string>)
}

export function AccountPanel() {
  const { authenticated, setAuthenticated } = useAppStore()
  const [summary, setSummary] = useState<AccountSummary | null>(null)
  const [drafts, setDrafts] = useState<Record<Provider, string>>(() => createEmptyDrafts())
  const [rotationGraceDays, setRotationGraceDays] = useState<Record<Provider, number>>({
    anthropic: 7,
    minimax: 7,
    zai: 7,
    github: 7
  })
  const [deliveryHistory, setDeliveryHistory] = useState<DeliveryHistoryEntry[]>([])
  const [rotationHistory, setRotationHistory] = useState<CredentialRotationHistory[]>([])
  const [exportFields, setExportFields] = useState<string[]>(DEFAULT_EXPORT_FIELDS)
  const [availableExportFields, setAvailableExportFields] = useState<string[]>(DEFAULT_EXPORT_FIELDS)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [savingProvider, setSavingProvider] = useState<Provider | null>(null)
  const [error, setError] = useState('')

  const credentialMap = useMemo(
    () => buildCredentialMap(summary?.credentials || []),
    [summary]
  )

  const loadAccount = useCallback(async () => {
    setError('')
    try {
      const [account, deliveries, rotations, exportOptions] = await Promise.all([
        ralphApi.getAccountSummary(),
        ralphApi.getDeliveryHistory(),
        ralphApi.getCredentialRotations(),
        ralphApi.getExportOptions()
      ])
      setSummary(account)
      setDeliveryHistory(deliveries)
      setRotationHistory(rotations)
      setAvailableExportFields(exportOptions.deliveryHistoryFields)
      setExportFields((current) => current.filter((field) => exportOptions.deliveryHistoryFields.includes(field)).length
        ? current.filter((field) => exportOptions.deliveryHistoryFields.includes(field))
        : exportOptions.deliveryHistoryFields.slice(0, DEFAULT_EXPORT_FIELDS.length))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load account settings.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    if (!authenticated) return
    void loadAccount()
  }, [authenticated, loadAccount])

  const saveCredential = useCallback(async (provider: Provider) => {
    const secret = drafts[provider]?.trim()
    if (!secret) {
      setError(`Enter a secret for ${PROVIDER_LABELS[provider]}.`)
      return
    }

    setSavingProvider(provider)
    setError('')
    try {
      await ralphApi.saveCredential(provider, secret)
      setDrafts((state) => ({ ...state, [provider]: '' }))
      await loadAccount()
    } catch (err) {
      setError(err instanceof Error ? err.message : `Unable to save ${PROVIDER_LABELS[provider]} credential.`)
    } finally {
      setSavingProvider(null)
    }
  }, [drafts, loadAccount])

  const rotateCredential = useCallback(async (provider: Provider) => {
    const secret = drafts[provider]?.trim()
    if (!secret) {
      setError(`Enter a new secret for ${PROVIDER_LABELS[provider]}.`)
      return
    }

    setSavingProvider(provider)
    setError('')
    try {
      await ralphApi.rotateCredential(provider, secret, rotationGraceDays[provider])
      setDrafts((state) => ({ ...state, [provider]: '' }))
      await loadAccount()
    } catch (err) {
      setError(err instanceof Error ? err.message : `Unable to rotate ${PROVIDER_LABELS[provider]} credential.`)
    } finally {
      setSavingProvider(null)
    }
  }, [drafts, loadAccount, rotationGraceDays])

  const removeCredential = useCallback(async (provider: Provider) => {
    setSavingProvider(provider)
    setError('')
    try {
      await ralphApi.deleteCredential(provider)
      setDrafts((state) => ({ ...state, [provider]: '' }))
      await loadAccount()
    } catch (err) {
      setError(err instanceof Error ? err.message : `Unable to remove ${PROVIDER_LABELS[provider]} credential.`)
    } finally {
      setSavingProvider(null)
    }
  }, [loadAccount])

  const handleLogout = useCallback(async () => {
    setError('')
    try {
      await ralphApi.logout()
      setAuthenticated(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to log out.')
    }
  }, [setAuthenticated])

  const toggleExportField = (field: string) => {
    setExportFields((current) =>
      current.includes(field)
        ? current.filter((entry) => entry !== field)
        : [...current, field]
    )
  }

  const exportCsv = useCallback(async () => {
    setError('')
    try {
      const blob = await ralphApi.exportDeliveryHistoryCsv(exportFields)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = 'delivery-history.csv'
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to export delivery history.')
    }
  }, [exportFields])

  if (!authenticated) return null

  return (
    <div className="card account-panel">
      <div className="card-header">
        <span className="card-title">Account / Credentials</span>
        <div className="account-header-actions">
          <button
            type="button"
            className="btn-small"
            onClick={() => {
              setRefreshing(true)
              void loadAccount()
            }}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button type="button" className="btn-small btn-danger" onClick={() => void handleLogout()}>
            Logout
          </button>
        </div>
      </div>

      {summary?.user && (
        <div className="account-summary">
          <div className="account-user">
            <span className="account-user-label">Session</span>
            <span className="account-user-email">{summary.user.email}</span>
            <span className="account-user-id">ID: {summary.user.id}</span>
          </div>
          <span className="status-pill is-ok">authenticated</span>
        </div>
      )}

      <div className="account-provider-grid">
        {PROVIDERS.map((provider) => {
          const status = credentialMap[provider]
          const isSaving = savingProvider === provider
          return (
            <div key={provider} className={`credential-card ${status?.configured ? 'is-configured' : 'is-missing'}`}>
              <div className="credential-head">
                <div>
                  <div className="credential-title">{PROVIDER_LABELS[provider]}</div>
                  <div className="credential-sub">{formatDate(status?.updatedAt)}</div>
                </div>
                <span className={`status-pill ${status?.configured ? 'is-ok' : 'is-warn'}`}>
                  {status?.configured ? 'configured' : 'missing'}
                </span>
              </div>

              <label className="auth-field credential-field">
                <span>Secret</span>
                <input
                  type="password"
                  className="config-input login-input credential-input"
                  value={drafts[provider]}
                  onChange={(event) => setDrafts((current) => ({ ...current, [provider]: event.target.value }))}
                  placeholder={status?.configured ? 'replace or rotate secret' : 'enter secret'}
                  autoComplete="off"
                />
              </label>

              <label className="auth-field credential-field">
                <span>Grace days</span>
                <input
                  type="number"
                  min={0}
                  max={30}
                  className="config-input"
                  value={rotationGraceDays[provider]}
                  onChange={(event) => setRotationGraceDays((current) => ({
                    ...current,
                    [provider]: Math.max(0, Math.min(30, Number(event.target.value) || 0))
                  }))}
                />
              </label>

              <div className="credential-actions">
                <button
                  type="button"
                  className="btn-small btn-primary"
                  onClick={() => void saveCredential(provider)}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : (status?.configured ? 'Replace' : 'Save')}
                </button>
                <button
                  type="button"
                  className="btn-small"
                  onClick={() => void rotateCredential(provider)}
                  disabled={isSaving || !status?.configured}
                >
                  Rotate
                </button>
                <button
                  type="button"
                  className="btn-small btn-danger"
                  onClick={() => void removeCredential(provider)}
                  disabled={isSaving || !status?.configured}
                >
                  Remove
                </button>
              </div>
            </div>
          )
        })}
      </div>

      <div className="account-section">
        <div className="card-header">
          <span className="card-title">Rotation History</span>
        </div>
        <div className="history-list">
          {rotationHistory.length === 0 && <div className="account-note">No credential rotation events yet.</div>}
          {rotationHistory.map((entry) => (
            <div key={entry.id} className="history-row">
              <div className="history-main">
                <span className="history-title">{PROVIDER_LABELS[entry.provider]} {entry.action}</span>
                <span className="history-sub">
                  {entry.previousFingerprint || 'none'} → {entry.nextFingerprint || 'none'}
                </span>
              </div>
              <div className="history-meta">
                <span>{formatDate(entry.createdAt)}</span>
                <span>{entry.graceUntil ? `grace until ${formatDate(entry.graceUntil)}` : 'no grace'}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="account-section">
        <div className="card-header">
          <span className="card-title">Delivery History</span>
          <button type="button" className="btn-small" onClick={() => void exportCsv()}>
            Export CSV
          </button>
        </div>
        <div className="export-field-grid">
          {availableExportFields.map((field) => (
            <label key={field} className="export-field-option">
              <input
                type="checkbox"
                checked={exportFields.includes(field)}
                onChange={() => toggleExportField(field)}
              />
              <span>{field}</span>
            </label>
          ))}
        </div>
        <div className="history-list">
          {deliveryHistory.length === 0 && <div className="account-note">No deliveries recorded yet.</div>}
          {deliveryHistory.map((entry) => (
            <div key={entry.id} className="history-row">
              <div className="history-main">
                <span className="history-title">{entry.deliveryType}</span>
                <span className="history-sub">{entry.target || 'no target'}</span>
              </div>
              <div className="history-meta">
                <span className={`status-pill ${entry.status === 'success' ? 'is-ok' : 'is-warn'}`}>{entry.status}</span>
                <span>{formatDate(entry.createdAt)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="account-note">
        Credentials are encrypted in D1. Rotation keeps the previous key alive for the configured grace period.
      </div>

      {loading && <div className="account-note">Loading account details...</div>}
      {error && <div className="account-error">{error}</div>}
    </div>
  )
}
